import {
    ButtonGroup,
    InternalGrid,
    ModalControl,
    ModalProvider,
} from '@paygreen/paygreen-ui';
import { ApiResponse, Country } from 'api-green-node';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormProvider, useForm } from 'react-hook-form';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    ActionBadge,
    CancelButton,
    CardClassic,
    EditButton,
    ErrorBoundary,
    H1Title,
    HideAndCopy,
    ValidateButton,
} from '../../../components';
import { useAPIError, useAuth, useUser } from '../../../context';
import { IsDesktop, IsMobile } from '../../../utils/tools';
import { patchMyUser } from '../service';
import { countryOptions } from '../shared';
import PasswordEditModal from './PasswordEditModal';
import UserLoginCredentials from './UserLoginCredentials';
import UserProfileForm from './UserProfileForm';

const UserProfile = (props) => {
    const { t } = props;
    const { getSdk } = useAuth();
    const { addError } = useAPIError();
    const { user, setUser } = useUser();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [userData, setUserData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);

    // initializing all react-hook-form values and methods
    const defaultValues = {
        accountId: userData?.accountId,
        username: userData?.username,
        lastname: userData?.lastname,
        firstname: userData?.firstname,
        email: userData?.email,
        phone: userData?.phone?.slice(4),
        country: countryOptions(t)[Country[userData?.country]]?.value,
        role: userData?.role,
    };

    const methods = useForm({
        defaultValues,
    });

    const { register, handleSubmit, clearErrors, reset } = methods;

    // Watch for changes on inputs
    useEffect(() => {
        register({ name: 'lastname' });
        register({ name: 'firstname' });
        register({ name: 'phone' });
        register({ name: 'email' });
        register({ name: 'country' });
    }, [register]);

    useEffect(() => {
        // update default values for selector and controlled input in form when data is retrieved
        reset({
            phone: userData?.phone?.slice(4),
            country: userData.country,
        });
    }, [userData, reset]);

    /**
     * GET request to API to get user's informations
     * @returns {object} - object data about user
     */
    const getUser = useCallback(() => {
        getSdk()
            .user.getOne('me')
            .then((res) => {
                if (ApiResponse.isSuccessful(res)) {
                    isMounted.current && setUserData(res.dataInfo);
                } else {
                    isMounted.current &&
                        addError(
                            ApiResponse.getErrorMessage(res),
                            ApiResponse.getStatus(res),
                        );
                }
            })
            .finally(() => isMounted.current && setIsLoading(false));
    }, [getSdk, addError]);

    useEffect(() => {
        isMounted.current = true;

        getUser();

        return () => {
            isMounted.current = false;
        };
    }, [getUser]);

    /**
     * PATCH request to API to update user's data
     * @returns {object} - object with user's data updated
     */
    const updateUser = useCallback(
        (updatedUser) => {
            setIsLoading(true);

            patchMyUser(updatedUser)
                .then((res) => {
                    const {
                        firstname,
                        lastname,
                        username,
                        role,
                        country,
                        phone,
                        email,
                        accountId,
                    } = res.data;

                    setUser({
                        ...user,
                        firstname,
                        lastname,
                    });

                    setUserData({
                        username,
                        accountId,
                        role,
                        email,
                        firstname,
                        lastname,
                        phone,
                        country,
                    });

                    setIsEditing(false);

                    toast(t('form.toastSuccess.userUpdate'));
                })
                .catch((error) => {
                    const { detail, status } = error?.response?.data;

                    addError(detail, status);
                })
                .finally(() => setIsLoading(false));
        },
        [addError, setUser, user, setUserData, setIsLoading, t],
    );

    const submitForm = (data) => {
        const updatedUser = {
            lastname: data.lastname,
            firstname: data.firstname,
            phone: '0033' + data.phone.replace(/\(0\)|[\s]|\+33 /g, ''),
            email: data.email,
            country: data.country,
        };

        updateUser(updatedUser);
    };

    const resetForm = () => {
        clearErrors();
        reset();
        setIsEditing(false);
    };

    return (
        <>
            <Helmet>
                <title>
                    {t('user.see.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <H1Title title={t('user.see.title')} />
            <InternalGrid
                displayType="grid"
                gridGap="lg"
                gridTemplateColumns={IsDesktop() ? '1fr 1fr' : '1fr'}
                justifyItems="stretch"
                hasStaticWidth={false}
                childrenMarginTop="md"
            >
                <ErrorBoundary>
                    <CardClassic
                        title={t('user.see.information')}
                        titleSize="md"
                        isShadowWab
                        blockWidth="md"
                    >
                        <FormProvider {...methods}>
                            <UserProfileForm
                                onSubmit={handleSubmit(submitForm)}
                                defaultValues={defaultValues}
                                isLoading={isLoading}
                                setIsEditing={setIsEditing}
                            >
                                {isEditing && (
                                    <ButtonGroup
                                        marginTop="sm"
                                        marginBottom="sm"
                                    >
                                        <CancelButton
                                            buttonSize="md"
                                            onClick={() => resetForm()}
                                            dataCy="user-cancel"
                                        >
                                            {t('form.button.cancel')}
                                        </CancelButton>

                                        <ValidateButton
                                            buttonSize="md"
                                            isDisabled={isLoading}
                                            type="submit"
                                            dataCy="user-validate"
                                        >
                                            {t('resetPassword.validateButton')}
                                        </ValidateButton>
                                    </ButtonGroup>
                                )}
                            </UserProfileForm>
                        </FormProvider>

                        {!isEditing && (
                            <ButtonGroup marginTop="sm" marginBottom="sm">
                                <ActionBadge
                                    text={t('user.edit.badgetext')}
                                    className="action-badge-user"
                                />
                            </ButtonGroup>
                        )}
                    </CardClassic>
                </ErrorBoundary>

                <InternalGrid
                    displayType="grid"
                    gridGap="lg"
                    gridTemplateColumns="1fr"
                    hasStaticWidth={false}
                    alignItems="stretch"
                    justifyItems="stretch"
                    childrenMarginBottom={!IsDesktop() ? 'md' : 'none'}
                >
                    <ErrorBoundary>
                        <CardClassic
                            title={t('user.editPassword.editPasswordCardTitle')}
                            isShadowWab={true}
                            titleSize="md"
                            blockWidth="md"
                        >
                            <ModalProvider>
                                <ButtonGroup
                                    marginTop={IsMobile() ? 'sm' : 'none'}
                                >
                                    <ModalControl hasOnClickControl={true}>
                                        <EditButton buttonStyle="line">
                                            {t(
                                                'user.editPassword.modifyPasswordButton',
                                            )}
                                        </EditButton>
                                    </ModalControl>
                                </ButtonGroup>

                                <ModalControl>
                                    <PasswordEditModal
                                        setIsLoading={setIsLoading}
                                    />
                                </ModalControl>
                            </ModalProvider>
                        </CardClassic>
                    </ErrorBoundary>

                    <ErrorBoundary>
                        <UserLoginCredentials
                            isLoading={isLoading}
                            defaultValues={defaultValues}
                        />
                    </ErrorBoundary>
                    {!!userData?.api_secret && (
                        <CardClassic
                            title={t('user.apiKey.apiKeyCardTitle')}
                            blockWidth="lg"
                        >
                            <HideAndCopy
                                mode="hide"
                                text={userData?.api_secret}
                                startLength={5}
                            />
                        </CardClassic>
                    )}
                </InternalGrid>
            </InternalGrid>
        </>
    );
};

export default withTranslation('common', 'countries')(UserProfile);
